import browseFile from "../../../browseFile";
import csvToJson from "../../../csvToJson";
import unflatten from "../../../unflatten";
import jsonToObject from "../../../jsonToObject";
import BaseListPresenter from "../../../base/BaseListPresenter";
import { dialog } from "../../../nq-component";
import DialogCancel from "../components/DialogCancel";

class InquiriesListPresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    exportCSVUseCase,
    addSchemaUseCase,
    updateSchemaUseCase,
    deleteSchemaUseCase,
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.exportCSVUseCase = exportCSVUseCase;
    this.addSchemaUseCase = addSchemaUseCase;
    this.updateSchemaUseCase = updateSchemaUseCase;
    this.deleteSchemaUseCase = deleteSchemaUseCase;
  }

  init() {
    const user = this.view.getCurrentUser();
    this.limit = 20;
    this.where = {
      createdBy: user?.id,
    };
    this.search = {};
    this.filter = {};
    this.include = ["all"];
    this.keys = undefined; // if keys are specified, only those keys will be returned
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.reset();
  }

  componentDidUpdate(prevProps) {
    const query = this.view.getQuery();
    if (query?.where && prevProps?.query?.where !== query?.where) {
      this.reset();
      this.where = JSON.parse(query.where);
      this.getObjects();
      this.getAllObjects();
    }
  }

  async acceptApplication(index) {
    const object = this.objects[index];
    object.status = "Accepted";
    const collection = this.view.getCollectionName();
    try {
      const data = await this.upsertUseCase.execute(collection, object);

      if (data.status === object.status) {
        this.objects[index] = object;
        this.view.setObjects(this.objects);
      }
      console.log(data);
    } catch (error) {
      this.view.showError(error);
    }

    console.log(object);
  }

  async getObjects() {
    await super.getObjects();

    const now = new Date();

    for (let i = 0; i < this.objects.length; i++) {
      const object = this.objects[i];

      if (object.status === "Pending (Warm Lead)" && object.updated) {
        const updatedDate = new Date(object.updated);
        const diffInDays = (now - updatedDate) / (1000 * 60 * 60 * 24); //(milliseconds * seconds * minutes * hours)

        if (diffInDays > 14) {
          const collection = this.view.getCollectionName();
          const updatedInquiry = {
            id: object.id,
            status: "Pending (Cold Lead)",
          };

          try {
            const result = await this.upsertUseCase.execute(
              collection,
              updatedInquiry,
            );

            if (result.status === "Pending (Cold Lead)") {
              this.objects[i] = {
                ...this.objects[i],
                status: "Pending (Cold Lead)",
                updated: result.updated,
              };
            }
          } catch (error) {
            console.error(
              `Failed to downgrade status for inquiry ${object.id}`,
              error,
            );
          }
        }
      }
    }

    this.view.setObjects(this.objects);
  }

  async cancelInquiry(index) {
    const object = this.objects[index];

    try {
      dialog.fire({
        html: (
          <DialogCancel
            onConfirm={(reason) => {
              this.onActedCancelInquiry(object, index, reason);
              dialog.close();
            }}
            onCancel={() => dialog.close()}
          />
        ),
        footer: false,
      });
    } catch (error) {
      console.warn("Inquiry cancellation aborted or failed:", error);
      this.view.showError("Inquiry was not cancelled.");
    }
  }

  async onActedCancelInquiry(object, index, reason) {
    console.log("reason", reason);
    try {
      const updatedInquiry = {
        id: object.id,
        status: "Cancelled",
        reason: reason,
      };

      const collection = this.view.getCollectionName();
      const data = await this.upsertUseCase.execute(collection, updatedInquiry);

      if (data.status === "Cancelled") {
        this.objects[index] = {
          ...object,
          status: "Cancelled",
          reason: reason,
        };
        this.view.setObjects([...this.objects]);
        this.view.showSuccess("Inquiry cancelled successfully.");
      }
    } catch (error) {
      console.warn("Inquiry cancellation aborted or failed:", error);
      this.view.showError("Inquiry was not cancelled.");
    }
  }

  async closeDeal(index) {
    const object = this.objects[index];

    try {
      await this.view.showDialog({
        icon: "bi bi-check-circle",
        title: "Close Deal",
        positiveButton: "Confirm",
        negativeButton: "Cancel",
        type: "primary",
        message: "Are you sure you want to mark this inquiry as Closed Deal?",
      });
    } catch {
      // User cancelled dialog
      return;
    }

    const updatedInquiry = {
      id: object.id,
      status: "Closed Sale",
    };

    const collection = this.view.getCollectionName();

    try {
      const data = await this.upsertUseCase.execute(collection, updatedInquiry);

      if (data.status === "Closed Sale") {
        this.objects[index] = {
          ...object,
          status: "Closed Sale",
        };
        this.view.setObjects([...this.objects]);
        this.view.showSuccess("Sale Closed!");
      }
    } catch (error) {
      console.error("Failed to close deal:", error);
      this.view.showError("Failed to close deal.");
    }
  }

  getActionButtonsFor(index) {
    const object = this.objects[index];
    const status = object?.status || "Draft";
    const view = this.view;

    const buttons = {
      view: {
        label: "View",
        icon: "eye",
        onClick: () => view.onClickView(index),
        className: "btn-primary",
      },
      quotation: {
        label: "Quotation",
        icon: "send-fill",
        onClick: () => view.onClickInfo(index),
        className: "btn-dark",
      },
      closeDeal: {
        label: "Close Deal",
        icon: "check-circle",
        onClick: () => this.closeDeal(index),
        className: "btn-dark",
      },
      followUp: {
        label: "Follow Up",
        icon: "send-fill",
        onClick: () =>
          view.navigateTo(`/inquiries/quotation/follow-up/${object.id}`),
        className: "btn-warning",
      },
      download: {
        label: "Download",
        icon: "filetype-pdf",
        onClick: () => view.exportPDF(index),
        className: "btn-outline-dark text-black",
        style: { backgroundColor: "#cce3ed" },
      },
      cancel: {
        label: "Cancel",
        icon: "x-circle",
        onClick: () => this.cancelInquiry(index),
        className: "btn-danger",
      },
    };

    switch (status) {
      case "Draft":
        return [
          buttons.view,
          buttons.quotation,
          [buttons.cancel, buttons.download],
        ];
      case "Pending (Warm Lead)":
        return [
          buttons.view,
          buttons.closeDeal,
          [buttons.quotation, buttons.download, buttons.cancel],
        ];
      case "Pending (Cold Lead)":
        return [
          buttons.view,
          buttons.quotation,
          [buttons.followUp, buttons.download, buttons.cancel],
        ];
      case "Closed Deal":
        return [buttons.view, buttons.download];
      case "Cancelled":
        return [buttons.view, buttons.download];
      default:
        return [];
    }
  }
}

export default InquiriesListPresenter;
